import { useCallback, useEffect, useState } from 'react';
import { bytes, when } from '../../lib/n4';
import { type Site, errText, noteOf, pickList, post, siteApi, toMs } from './common';

interface Backup { id: number; path?: string; file?: string; exists?: boolean; bytes?: number; size?: number; note?: string | null; created_at?: number | string; url?: string }

// Mentések fül: „Mentés most” (a db_paths SQLite-jai), lista, letöltés, visszaállítás.
export function BackupsList({ site, onChanged }: { site: Site; onChanged: () => void }) {
  const [list, setList] = useState<Backup[] | null>(null);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [busy, setBusy] = useState<'new' | number | null>(null);
  const load = useCallback(() => {
    siteApi<unknown>(site.id, '/backups').then((r) => { setList(pickList<Backup>(r, 'backups')); setErr(''); }).catch((e) => setErr(errText(e, 'Mentések')));
  }, [site.id]);
  useEffect(() => { load(); }, [load]);

  const backup = async () => {
    setBusy('new'); setErr('');
    try { const r = await siteApi(site.id, '/backup', post()); setMsg(noteOf(r) || 'Mentés kész.'); load(); onChanged(); }
    catch (e) { setErr(errText(e, 'Mentés')); } finally { setBusy(null); }
  };
  const restore = async (b: Backup) => {
    if (!confirm(`Visszaállítod az adatbázist erre a mentésre?\n\n${new Date(toMs(b.created_at) ?? 0).toLocaleString('hu-HU')}${b.note ? ` — ${b.note}` : ''}\n\nA weboldal leáll, a mentés visszamásolódik, majd újraindul. A mentés óta keletkezett adat elveszik.`)) return;
    setBusy(b.id); setErr('');
    try { const r = await siteApi(site.id, '/restore', post({ backup_id: b.id })); setMsg(noteOf(r) || 'Visszaállítva, a weboldal újraindult.'); load(); onChanged(); }
    catch (e) { setErr(errText(e, 'Visszaállítás')); } finally { setBusy(null); }
  };
  const noDb = !(site.db_paths ?? []).length;

  return (
    <div>
      <div className="a-site-bar">
        <button className="a-btn sm solid" disabled={busy != null || noDb} onClick={() => void backup()} title={noDb ? 'Nincs megadott adatbázis (Beállítások → Adatbázisok)' : 'Az összes megadott adatbázis mentése most'}>{busy === 'new' ? 'Mentés…' : 'Mentés most'}</button>
        <span className="a-site-note">{noDb ? 'Nincs megadott adatbázis: a Beállítások → „Adatbázisok (mentéshez)” listába vedd fel az SQLite-fájlokat.' : `Mentve: ${(site.db_paths ?? []).join(', ')}`}</span>
      </div>
      {err && <div className="a-site-msg bad">{err}</div>}
      {msg && <div className="a-site-msg ok">{msg}</div>}
      {list && (
        <div className="a-site-tablewrap">
          <table className="a-site-table">
            <thead><tr><th>Idő</th><th className="num">Méret</th><th>Megjegyzés</th><th className="acts" /></tr></thead>
            <tbody>
              {list.map((b) => (
                <tr key={b.id}>
                  <td className="tm">{when(toMs(b.created_at))}</td>
                  <td className="num">{bytes(b.bytes ?? b.size)}</td>
                  <td title={b.path}>{b.note || <span className="mut">—</span>}{(b.file || b.path) && <div className="sub mono">{b.file || b.path?.split('/').pop()}{b.exists === false ? ' — a fájl hiányzik' : ''}</div>}</td>
                  <td className="acts">
                    {b.exists !== false && <a className="a-btn sm ghost" href={b.url ?? `/api/n4/sites/${site.id}/backups/${b.id}/download`} download>↓ Letöltés</a>}
                    <button className="a-btn sm" disabled={busy != null || b.exists === false} onClick={() => void restore(b)}>{busy === b.id ? 'Visszaállítás…' : 'Visszaállítás'}</button>
                  </td>
                </tr>
              ))}
              {list.length === 0 && <tr className="empty"><td colSpan={4}>Még nincs mentés.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
      {!list && !err && <p className="a-sub">betöltés…</p>}
    </div>
  );
}
